import { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router";
import { Box, Button, CircularProgress, Container, Stack, Typography } from "@mui/material";

import learnApi from "../../api/learnApi";
import LearnArticleContent from "../../components/learn/LearnArticleContent";
import LearnBreadcrumbs from "../../components/learn/LearnBreadcrumbs";
import RelatedLearnArticles from "../../components/learn/RelatedLearnArticles";

function applyLearnArticleSeo(article) {
  document.title = article.seo_title || `${article.title} | ResourceNAV Learn`;
  let description = document.querySelector('meta[name="description"]');
  if (!description) {
    description = document.createElement("meta");
    description.setAttribute("name", "description");
    document.head.appendChild(description);
  }
  description.setAttribute("content", article.seo_description || article.description || "");
}

function getArticleCta(article) {
  if (article?.article_type === "metric_concept") {
    return {
      title: "Put the analysis into practice",
      description:
        "Use ResourceNAV to compare mining companies by lifecycle, jurisdiction, financial strength, asset context and other factors that shape the investment case.",
    };
  }

  return {
    title: "Put this framework into practice",
    description:
      "Use ResourceNAV to compare mining companies by lifecycle, jurisdiction, financial strength, asset context and other factors that shape the investment case.",
  };
}

function articleDeckLines(description = "") {
  const marker = " This article ";
  const index = description.indexOf(marker);
  if (index === -1) return [description];
  return [description.slice(0, index).trim(), `This article ${description.slice(index + marker.length)}`];
}

function LearnArticleCta({ article }) {
  const cta = getArticleCta(article);

  return (
    <Box
      sx={{
        mt: 2,
        position: "relative",
        overflow: "hidden",
        py: { xs: 6, md: 7.5 },
        px: { xs: 2.5, md: 5 },
        textAlign: "center",
        background: "linear-gradient(120deg, rgba(38,38,38,0.92), rgba(201, 176, 123,0.9))",
        color: "white",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 20% 20%, rgba(227,154,74,0.18), transparent 35%), radial-gradient(circle at 80% 0%, rgba(67,129,193,0.2), transparent 30%)",
          pointerEvents: "none",
        }}
      />
      <Typography component="h2" variant="h4" fontWeight={900} sx={{ mb: 2, position: "relative", zIndex: 1 }}>
        {cta.title}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          maxWidth: 760,
          mx: "auto",
          mb: 3,
          lineHeight: 1.7,
          color: "rgba(255,255,255,0.85)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {cta.description}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="center"
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Button component={RouterLink} to="/companies" variant="contained" size="large" sx={{ px: 5 }}>
          Explore rankings
        </Button>
        <Button
          component={RouterLink}
          to="/screener"
          variant="outlined"
          size="large"
          sx={{
            px: 5,
            color: "white",
            borderColor: "rgba(255,255,255,0.72)",
            "&:hover": {
              borderColor: "white",
              backgroundColor: "rgba(255,255,255,0.10)",
            },
          }}
        >
          Open screener
        </Button>
      </Stack>
    </Box>
  );
}

export function LearnArticleResearchCTA() {
  const { slug } = useParams();
  const [article, setArticle] = useState({ slug, article_type: "metric_concept" });

  useEffect(() => {
    let ignore = false;
    learnApi
      .getLearnArticle(slug)
      .then((data) => {
        if (!ignore) setArticle(data);
      })
      .catch(() => {
        if (!ignore) setArticle({ slug, article_type: "metric_concept" });
      });
    return () => {
      ignore = true;
    };
  }, [slug]);

  return <LearnArticleCta article={article} />;
}

export default function LearnArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError("");
    setNotFound(false);
    learnApi
      .getLearnArticle(slug)
      .then((data) => {
        if (!ignore) {
          setArticle(data);
          applyLearnArticleSeo(data);
        }
      })
      .catch((err) => {
        if (ignore) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          setError("This Learn article is unavailable right now.");
        }
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [slug]);

  return (
    <Container maxWidth={false} sx={{ maxWidth: 1120, mt: { xs: 4, md: 5 }, mb: 8 }}>
      {loading ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : notFound ? (
        <Box sx={{ py: 6, maxWidth: 720 }}>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Learn article not found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1.25 }}>
            The article may not be published yet or may have moved.
          </Typography>
          <Button component={RouterLink} to="/learn" variant="contained" sx={{ mt: 3 }}>
            Back to Learn
          </Button>
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : article ? (
        <Box>
          <Box sx={{ mb: 2 }}>
            <LearnBreadcrumbs article={article} />
          </Box>

          <Box
            sx={(theme) => ({
              ...theme.mixins.frostedBox,
              px: { xs: 2.25, sm: 3, md: 5 },
              py: { xs: 3, md: 5 },
              background: "rgba(255, 255, 255, 0.74)",
            })}
          >
            <Box>
              <Typography component="h1" sx={{ fontSize: { xs: 32, md: 43 }, fontWeight: 900, lineHeight: 1.08 }}>
                {article.title}
              </Typography>
              {article.description ? (
                <Typography variant="body1" color="text.primary" sx={{ mt: 1.5, lineHeight: 1.75, fontSize: 16 }}>
                  {articleDeckLines(article.description).map((line, index) => (
                    <Box
                      key={line}
                      component="span"
                      sx={{ display: "block", mt: index === 0 ? 0 : 1 }}
                    >
                      {line}
                    </Box>
                  ))}
                </Typography>
              ) : null}
            </Box>

            <Box sx={{ mt: 4, pt: 4, borderTop: "1px solid rgba(34, 35, 37, 0.16)" }}>
              <Box>
                <LearnArticleContent content={article.content_markdown} visual={article.visual} />
              </Box>
            </Box>

            <Box className="learn-resource-links-slot" />
          </Box>

          <Box>
            <RelatedLearnArticles relatedArticles={article.related_articles} />
          </Box>
        </Box>
      ) : null}
    </Container>
  );
}
